import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Pencil, Trash2, ChefHat, ArrowLeft, GripVertical,
  Image as ImageIcon, Search, EyeOff, X, Save, Loader2, Upload, PackagePlus, Sparkles, Star, Tag, Copy, FileDown
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { usePlanLimits } from "@/hooks/usePlanLimits";
import { ImportMenuDialog } from "@/components/menu/ImportMenuDialog";
import { CopyAddonsDialog } from "@/components/menu/CopyAddonsDialog";
import type { Tables } from "@/integrations/supabase/types";

type Category = Tables<"categories">;
type Product = Tables<"products">;

interface AddonCategory {
  id?: string;
  name: string;
  is_required: boolean;
  min_select: number;
  max_select: number;
  sort_order: number;
  addons: Addon[];
  _isNew?: boolean;
  _deleted?: boolean;
}

interface Addon {
  id?: string;
  name: string;
  price: number;
  is_available: boolean;
  image_url: string;
  sort_order: number;
  _isNew?: boolean;
  _deleted?: boolean;
}

const GerenciarCardapio = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const planLimits = usePlanLimits(restaurantId);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  // Category modal
  const [catModal, setCatModal] = useState(false);
  const [catEditing, setCatEditing] = useState<Category | null>(null);
  const [catForm, setCatForm] = useState({ name: "", description: "" });
  const [catSaving, setCatSaving] = useState(false);

  // Product modal
  const [prodModal, setProdModal] = useState(false);
  const [prodEditing, setProdEditing] = useState<Product | null>(null);
  const [prodForm, setProdForm] = useState({
    name: "", description: "", price: "", discount_price: "", category_id: "", image_url: "",
    is_available: true, is_featured: false, preparation_time_min: "",
  });
  const [prodSaving, setProdSaving] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [addonCategories, setAddonCategories] = useState<AddonCategory[]>([]);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [addonImageUploading, setAddonImageUploading] = useState<string | null>(null);
  // Delete
  const [deleteTarget, setDeleteTarget] = useState<{ type: "category" | "product"; id: string; name: string } | null>(null);
  // Import menu
  const [importMenuOpen, setImportMenuOpen] = useState(false);
  // Copy addons
  const [copyAddonsTarget, setCopyAddonsTarget] = useState<{ id: string; name: string } | null>(null);
  const [copyAddonsToForm, setCopyAddonsToForm] = useState(false);

  const fetchData = useCallback(async (restId: string) => {
    const [catRes, prodRes] = await Promise.all([
      supabase.from("categories").select("*").eq("restaurant_id", restId).order("sort_order"),
      supabase.from("products").select("*").eq("restaurant_id", restId).order("sort_order"),
    ]);
    setCategories(catRes.data || []);
    setProducts(prodRes.data || []);
  }, []);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase.from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase.from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      await fetchData(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, fetchData]);

  // ========== CATEGORY CRUD ==========
  const openCatModal = (cat?: Category) => {
    if (!cat && !planLimits.canAddCategory) {
      toast({ title: "Limite atingido", description: `Seu plano (${planLimits.planName}) permite no máximo ${planLimits.maxCategories} categorias. Faça upgrade para criar mais.`, variant: "destructive" });
      return;
    }
    if (cat) {
      setCatEditing(cat);
      setCatForm({ name: cat.name, description: cat.description || "" });
    } else {
      setCatEditing(null);
      setCatForm({ name: "", description: "" });
    }
    setCatModal(true);
  };

  const saveCat = async () => {
    if (!catForm.name.trim() || !restaurantId) return;
    setCatSaving(true);
    if (catEditing) {
      const { error } = await supabase.from("categories").update({
        name: catForm.name.trim(),
        description: catForm.description.trim() || null,
      }).eq("id", catEditing.id);
      if (error) { toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" }); }
      else toast({ title: "Categoria atualizada!" });
    } else {
      const { error } = await supabase.from("categories").insert({
        restaurant_id: restaurantId,
        name: catForm.name.trim(),
        description: catForm.description.trim() || null,
        sort_order: categories.length,
      });
      if (error) { toast({ title: "Erro ao criar", description: error.message, variant: "destructive" }); }
      else toast({ title: "Categoria criada!" });
    }
    setCatSaving(false);
    setCatModal(false);
    await fetchData(restaurantId);
  };

  // ========== PRODUCT CRUD ==========
  const openProdModal = async (prod?: Product) => {
    if (!prod && !planLimits.canAddProduct) {
      toast({ title: "Limite atingido", description: `Seu plano (${planLimits.planName}) permite no máximo ${planLimits.maxProducts} produtos. Faça upgrade para criar mais.`, variant: "destructive" });
      return;
    }
    if (prod) {
      setProdEditing(prod);
      setProdForm({
        name: prod.name,
        description: prod.description || "",
        price: String(prod.price),
        discount_price: (prod as any).discount_price ? String((prod as any).discount_price) : "",
        category_id: prod.category_id || "",
        image_url: prod.image_url || "",
        is_available: prod.is_available,
        is_featured: prod.is_featured,
        preparation_time_min: prod.preparation_time_min ? String(prod.preparation_time_min) : "",
      });
      setImagePreview(prod.image_url || null);
      // Load addon categories with addons
      const { data: cats } = await supabase.from("addon_categories" as any).select("*").eq("product_id", prod.id).order("sort_order");
      const { data: addonsData } = await supabase.from("product_addons").select("*").eq("product_id", prod.id).order("sort_order");
      
      const catList = (cats || []) as any[];
      const addonList = (addonsData || []) as any[];
      
      // Group addons by category
      const addonCats: AddonCategory[] = catList.map((c: any) => ({
        id: c.id,
        name: c.name,
        is_required: c.is_required,
        min_select: c.min_select,
        max_select: c.max_select,
        sort_order: c.sort_order,
        addons: addonList.filter((a: any) => a.addon_category_id === c.id).map((a: any) => ({
          id: a.id, name: a.name, price: Number(a.price), is_available: a.is_available, image_url: a.image_url || "", sort_order: a.sort_order,
        })),
      }));
      
      // Uncategorized addons (legacy)
      const uncategorized = addonList.filter((a: any) => !a.addon_category_id);
      if (uncategorized.length > 0) {
        addonCats.push({
          name: "Adicionais",
          is_required: false,
          min_select: 0,
          max_select: 10,
          sort_order: addonCats.length,
          _isNew: true,
          addons: uncategorized.map((a: any) => ({
            id: a.id, name: a.name, price: Number(a.price), is_available: a.is_available, image_url: a.image_url || "", sort_order: a.sort_order,
          })),
        });
      }
      
      setAddonCategories(addonCats);
    } else {
      setProdEditing(null);
      setProdForm({ name: "", description: "", price: "", discount_price: "", category_id: selectedCategoryId || "", image_url: "", is_available: true, is_featured: false, preparation_time_min: "" });
      setImagePreview(null);
      setAddonCategories([]);
    }
    setImageFile(null);
    setProdModal(true);
  };

  const addAddonCategory = () => {
    setAddonCategories(prev => [...prev, {
      name: "", is_required: false, min_select: 0, max_select: 1,
      sort_order: prev.length, addons: [], _isNew: true,
    }]);
  };

  const updateAddonCategory = (catIdx: number, field: keyof AddonCategory, value: any) => {
    setAddonCategories(prev => prev.map((c, i) => i === catIdx ? { ...c, [field]: value } : c));
  };

  const removeAddonCategory = (catIdx: number) => {
    setAddonCategories(prev => {
      const cat = prev[catIdx];
      if (cat.id) return prev.map((c, i) => i === catIdx ? { ...c, _deleted: true } : c);
      return prev.filter((_, i) => i !== catIdx);
    });
  };

  const addAddonToCategory = (catIdx: number) => {
    setAddonCategories(prev => prev.map((c, i) => i === catIdx ? {
      ...c,
      addons: [...c.addons, { name: "", price: 0, is_available: true, image_url: "", sort_order: c.addons.length, _isNew: true }],
    } : c));
  };

  const updateAddonInCategory = (catIdx: number, addonIdx: number, field: keyof Addon, value: any) => {
    setAddonCategories(prev => prev.map((c, i) => i === catIdx ? {
      ...c,
      addons: c.addons.map((a, j) => j === addonIdx ? { ...a, [field]: value } : a),
    } : c));
  };

  const removeAddonInCategory = (catIdx: number, addonIdx: number) => {
    setAddonCategories(prev => prev.map((c, i) => {
      if (i !== catIdx) return c;
      const addon = c.addons[addonIdx];
      if (addon.id) {
        return { ...c, addons: c.addons.map((a, j) => j === addonIdx ? { ...a, _deleted: true } : a) };
      }
      return { ...c, addons: c.addons.filter((_, j) => j !== addonIdx) };
    }));
  };

  const handleAddonImageUpload = async (catIdx: number, addonIdx: number, file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Imagem muito grande", description: "Máximo 5MB", variant: "destructive" });
      return;
    }
    const key = `${catIdx}-${addonIdx}`;
    setAddonImageUploading(key);
    const url = await uploadImage(file);
    if (url) {
      updateAddonInCategory(catIdx, addonIdx, "image_url", url);
    }
    setAddonImageUploading(null);
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Imagem muito grande", description: "Máximo 5MB", variant: "destructive" });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const ext = file.name.split(".").pop();
    const path = `${restaurantId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from("product-images").upload(path, file);
    if (error) {
      toast({ title: "Erro no upload", description: error.message, variant: "destructive" });
      return null;
    }
    const { data: { publicUrl } } = supabase.storage.from("product-images").getPublicUrl(path);
    return publicUrl;
  };

  const saveProd = async () => {
    if (!prodForm.name.trim() || !prodForm.price || !restaurantId) {
      toast({ title: "Preencha nome e preço", variant: "destructive" });
      return;
    }
    setProdSaving(true);

    let imageUrl = prodForm.image_url.trim() || null;
    if (imageFile) {
      const uploaded = await uploadImage(imageFile);
      if (uploaded) imageUrl = uploaded;
      else { setProdSaving(false); return; }
    }

    const payload = {
      name: prodForm.name.trim(),
      description: prodForm.description.trim() || null,
      price: parseFloat(prodForm.price),
      discount_price: prodForm.discount_price ? parseFloat(prodForm.discount_price) : null,
      category_id: prodForm.category_id && prodForm.category_id !== "none" ? prodForm.category_id : null,
      image_url: imageUrl,
      is_available: prodForm.is_available,
      is_featured: prodForm.is_featured,
      preparation_time_min: prodForm.preparation_time_min ? parseInt(prodForm.preparation_time_min) : null,
    };

    let productId = prodEditing?.id;

    if (prodEditing) {
      const { error } = await supabase.from("products").update(payload).eq("id", prodEditing.id);
      if (error) { toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" }); setProdSaving(false); return; }
    } else {
      const { data: newProd, error } = await supabase.from("products").insert({
        ...payload,
        restaurant_id: restaurantId,
        sort_order: products.length,
      }).select("id").single();
      if (error || !newProd) { toast({ title: "Erro ao criar", description: error?.message, variant: "destructive" }); setProdSaving(false); return; }
      productId = newProd.id;
    }

    // Save addon categories and addons
    if (productId) {
      for (const cat of addonCategories) {
        if (cat._deleted) {
          if (cat.id) {
            // Delete addons first, then category
            await supabase.from("product_addons").delete().eq("addon_category_id", cat.id);
            await supabase.from("addon_categories" as any).delete().eq("id", cat.id);
          }
          continue;
        }
        
        let catId = cat.id;
        const catPayload = {
          product_id: productId,
          restaurant_id: restaurantId,
          name: cat.name.trim() || "Adicionais",
          is_required: cat.is_required,
          min_select: cat.is_required ? Math.max(1, cat.min_select) : cat.min_select,
          max_select: cat.max_select,
          sort_order: cat.sort_order,
        };
        
        if (cat.id) {
          await supabase.from("addon_categories" as any).update(catPayload).eq("id", cat.id);
        } else {
          const { data: newCat } = await supabase.from("addon_categories" as any).insert(catPayload).select("id").single();
          catId = (newCat as any)?.id;
        }
        
        if (!catId) continue;
        
        // Save addons in this category
        for (const addon of cat.addons) {
          if (addon._deleted) {
            if (addon.id) await supabase.from("product_addons").delete().eq("id", addon.id);
            continue;
          }
          if (!addon.name.trim()) continue;
          
          const addonPayload = {
            product_id: productId,
            addon_category_id: catId,
            name: addon.name.trim(),
            price: addon.price,
            is_available: addon.is_available,
            image_url: addon.image_url || null,
            sort_order: addon.sort_order,
            min_qty: 0,
            max_qty: 1,
          };
          
          if (addon.id) {
            await supabase.from("product_addons").update(addonPayload).eq("id", addon.id);
          } else {
            await supabase.from("product_addons").insert(addonPayload);
          }
        }
      }
    }

    toast({ title: prodEditing ? "Produto atualizado!" : "Produto criado!" });
    setProdSaving(false);
    setProdModal(false);
    await fetchData(restaurantId);
  };

  // ========== DELETE ==========
  const confirmDelete = async () => {
    if (!deleteTarget || !restaurantId) return;
    const table = deleteTarget.type === "category" ? "categories" : "products";
    const { error } = await supabase.from(table).delete().eq("id", deleteTarget.id);
    if (error) toast({ title: "Erro ao excluir", description: error.message, variant: "destructive" });
    else toast({ title: `${deleteTarget.type === "category" ? "Categoria" : "Produto"} excluído!` });
    setDeleteTarget(null);
    if (deleteTarget.type === "category" && selectedCategoryId === deleteTarget.id) {
      setSelectedCategoryId(null);
    }
    await fetchData(restaurantId);
  };

  // ========== TOGGLE AVAILABILITY ==========
  const toggleAvailability = async (prod: Product) => {
    if (!restaurantId) return;
    await supabase.from("products").update({ is_available: !prod.is_available }).eq("id", prod.id);
    await fetchData(restaurantId);
  };

  // ========== FILTERED DATA ==========
  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = selectedCategoryId ? p.category_id === selectedCategoryId : true;
    return matchesSearch && matchesCategory;
  });

  // Count addons per product (we'll fetch lazily)
  const [productAddonCounts, setProductAddonCounts] = useState<Record<string, { variants: number; addons: number }>>({});

  useEffect(() => {
    if (!restaurantId || products.length === 0) return;
    const fetchAddonCounts = async () => {
      const { data } = await supabase
        .from("product_addons")
        .select("product_id, id")
        .in("product_id", products.map(p => p.id));
      if (!data) return;
      const counts: Record<string, { variants: number; addons: number }> = {};
      data.forEach(a => {
        if (!counts[a.product_id]) counts[a.product_id] = { variants: 0, addons: 0 };
        counts[a.product_id].addons++;
      });
      setProductAddonCounts(counts);
    };
    fetchAddonCounts();
  }, [restaurantId, products]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-primary animate-spin" />
          </div>
          <p className="text-sm text-muted-foreground">Carregando cardápio...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} className="rounded-xl h-9 w-9">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <ChefHat className="h-4 w-4 text-primary" />
              </div>
              <div>
                <h1 className="font-display font-bold text-sm md:text-base">Gerenciar Cardápio</h1>
                <p className="text-[10px] text-muted-foreground hidden sm:block">{products.length} produtos • {categories.length} categorias</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto flex min-h-[calc(100vh-3.5rem)]">
        {/* ========== SIDEBAR CATEGORIES ========== */}
        <aside className="w-48 md:w-56 shrink-0 border-r border-border/50 bg-card/50 p-4 space-y-3 hidden md:block">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-foreground">Categorias</h2>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-primary hover:text-primary gap-1 px-2"
              onClick={() => openCatModal()}
            >
              <Plus className="h-3.5 w-3.5" /> Nova
            </Button>
          </div>

          <div className="space-y-1">
            <button
              onClick={() => setSelectedCategoryId(null)}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all truncate ${
                selectedCategoryId === null
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              Todos ({products.length})
            </button>
            {categories.map((cat) => {
              const count = products.filter(p => p.category_id === cat.id).length;
              return (
                <div key={cat.id} className="group relative">
                  <button
                    onClick={() => setSelectedCategoryId(cat.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all truncate flex items-center gap-2 ${
                      selectedCategoryId === cat.id
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                    }`}
                  >
                    <GripVertical className={`h-3 w-3 shrink-0 ${selectedCategoryId === cat.id ? "text-primary-foreground/50" : "text-muted-foreground/40"}`} />
                    <span className="truncate">{cat.name}</span>
                  </button>
                  {/* Edit/Delete on hover */}
                  <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center gap-0.5">
                    <button
                      onClick={(e) => { e.stopPropagation(); openCatModal(cat); }}
                      className="p-1 rounded hover:bg-secondary/80"
                    >
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget({ type: "category", id: cat.id, name: cat.name }); }}
                      className="p-1 rounded hover:bg-destructive/10"
                    >
                      <Trash2 className="h-3 w-3 text-destructive" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No category products */}
          {products.some(p => !p.category_id) && (
            <button
              onClick={() => setSelectedCategoryId("uncategorized")}
              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all truncate ${
                selectedCategoryId === "uncategorized"
                  ? "bg-primary text-primary-foreground shadow-sm font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
              }`}
            >
              Sem categoria
            </button>
          )}
        </aside>

        {/* ========== MAIN CONTENT ========== */}
        <main className="flex-1 p-4 md:p-6 space-y-4">
          {/* Mobile category selector */}
          <div className="md:hidden">
            <Select
              value={selectedCategoryId || "all"}
              onValueChange={(v) => setSelectedCategoryId(v === "all" ? null : v)}
            >
              <SelectTrigger className="bg-card border-border/60 rounded-xl h-10">
                <SelectValue placeholder="Todas as categorias" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as categorias ({products.length})</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search + New Product */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar produto..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-card border-border/60 rounded-xl h-10"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2">
                  <X className="h-3.5 w-3.5 text-muted-foreground" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setImportMenuOpen(true)}
              className="shrink-0 rounded-xl gap-2 shadow-sm"
            >
              <FileDown className="h-4 w-4" />
              <span className="hidden sm:inline">Importar</span>
            </Button>
            <Button
              onClick={() => openProdModal()}
              className="shrink-0 rounded-xl gap-2 shadow-sm"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Novo Produto</span>
              <span className="sm:hidden">Novo</span>
            </Button>
          </div>

          {/* ========== PRODUCTS GRID ========== */}
          {filteredProducts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border bg-card p-14 text-center">
              <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mx-auto mb-4">
                <ImageIcon className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="text-foreground font-medium">
                {search ? "Nenhum produto encontrado" : "Nenhum produto cadastrado"}
              </p>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                {search ? "Tente outro termo de busca" : "Adicione produtos ao seu cardápio"}
              </p>
              {!search && (
                <Button variant="outline" onClick={() => openProdModal()} className="rounded-xl gap-2">
                  <Plus className="h-4 w-4" /> Adicionar produto
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              <AnimatePresence>
                {filteredProducts.map((prod) => {
                  const addonInfo = productAddonCounts[prod.id];
                  return (
                    <motion.div
                      key={prod.id}
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      className={`group rounded-2xl border border-border/60 bg-card overflow-hidden hover:shadow-md transition-all ${
                        !prod.is_available ? "opacity-60" : ""
                      }`}
                    >
                      {/* Image */}
                      <div className="relative h-40 bg-secondary/30 overflow-hidden">
                        {prod.image_url ? (
                          <img
                            src={prod.image_url}
                            alt={prod.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <ImageIcon className="h-10 w-10 text-muted-foreground/20" />
                          </div>
                        )}
                        {prod.is_featured && (
                          <div className="absolute top-2 left-2">
                            <Badge className="text-[9px] h-5 px-1.5 bg-amber-500/90 text-white border-0 shadow-sm">
                              <Star className="h-2.5 w-2.5 mr-0.5 fill-white" /> Destaque
                            </Badge>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="p-4 space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-sm text-foreground truncate">{prod.name}</h3>
                            {prod.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2 leading-relaxed">{prod.description}</p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            {(prod as any).discount_price && Number((prod as any).discount_price) > 0 ? (
                              <div>
                                <p className="font-bold font-display text-base text-primary">
                                  R$ {Number((prod as any).discount_price).toFixed(2).replace(".", ",")}
                                </p>
                                <p className="text-[10px] text-muted-foreground line-through">
                                  R$ {Number(prod.price).toFixed(2).replace(".", ",")}
                                </p>
                              </div>
                            ) : (
                              <p className="font-bold font-display text-base text-primary">
                                R$ {Number(prod.price).toFixed(2).replace(".", ",")}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Badges row */}
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {addonInfo && addonInfo.addons > 0 && (
                            <Badge variant="secondary" className="text-[10px] h-5 px-2 font-normal">
                              {addonInfo.addons} add.
                            </Badge>
                          )}
                          <div className="flex-1" />
                          <Badge
                            variant="outline"
                            className={`text-[10px] h-5 px-2 ${
                              prod.is_available
                                ? "border-emerald-500/30 text-emerald-600 bg-emerald-500/5"
                                : "border-destructive/30 text-destructive bg-destructive/5"
                            }`}
                          >
                            {prod.is_available ? "Ativo" : "Inativo"}
                          </Badge>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center justify-between pt-2 border-t border-border/40">
                          <button
                            onClick={() => toggleAvailability(prod)}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                          >
                            <EyeOff className="h-3.5 w-3.5" />
                            {prod.is_available ? "Desativar" : "Ativar"}
                          </button>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg"
                              title="Copiar adicionais"
                              onClick={() => setCopyAddonsTarget({ id: prod.id, name: prod.name })}
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg"
                              onClick={() => openProdModal(prod)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 rounded-lg"
                              onClick={() => setDeleteTarget({ type: "product", id: prod.id, name: prod.name })}
                            >
                              <Trash2 className="h-3.5 w-3.5 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </main>
      </div>

      {/* ========== CATEGORY MODAL ========== */}
      <Dialog open={catModal} onOpenChange={setCatModal}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">
              {catEditing ? "Editar Categoria" : "Nova Categoria"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                placeholder="Ex: Hambúrgueres"
                value={catForm.name}
                onChange={(e) => setCatForm({ ...catForm, name: e.target.value })}
                className="bg-background border-border"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                placeholder="Descrição da categoria"
                value={catForm.description}
                onChange={(e) => setCatForm({ ...catForm, description: e.target.value })}
                className="bg-background border-border"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button onClick={saveCat} disabled={catSaving || !catForm.name.trim()} className="bg-gradient-primary text-primary-foreground">
              {catSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {catEditing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== PRODUCT MODAL ========== */}
      <Dialog open={prodModal} onOpenChange={setProdModal}>
        <DialogContent className="bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">
              {prodEditing ? "Editar Produto" : "Novo Produto"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                placeholder="Ex: X-Burger Especial"
                value={prodForm.name}
                onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })}
                className="bg-background border-border"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Descrição</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5"
                  disabled={generatingDesc || !prodForm.name.trim()}
                  onClick={async () => {
                    setGeneratingDesc(true);
                    try {
                      const categoryName = prodForm.category_id && prodForm.category_id !== "none"
                        ? categories.find(c => c.id === prodForm.category_id)?.name
                        : undefined;
                      const { data, error } = await supabase.functions.invoke("generate-description", {
                        body: {
                          productName: prodForm.name.trim(),
                          categoryName,
                          price: prodForm.price || undefined,
                          preparationTime: prodForm.preparation_time_min || undefined,
                        },
                      });
                      if (error) throw error;
                      if (data?.description) {
                        setProdForm(prev => ({ ...prev, description: data.description }));
                        toast({ title: "Descrição gerada com IA!" });
                      }
                    } catch (e: any) {
                      toast({ title: "Erro ao gerar descrição", description: e?.message || "Tente novamente", variant: "destructive" });
                    } finally {
                      setGeneratingDesc(false);
                    }
                  }}
                >
                  {generatingDesc ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  Gerar com IA
                </Button>
              </div>
              <textarea
                placeholder="Descreva o produto..."
                value={prodForm.description}
                onChange={(e) => setProdForm({ ...prodForm, description: e.target.value })}
                rows={2}
                className="w-full rounded-md bg-background border border-border px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Preço (R$) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  value={prodForm.price}
                  onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })}
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  Preço promo
                  <Tag className="h-3 w-3 text-destructive" />
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Opcional"
                  value={prodForm.discount_price}
                  onChange={(e) => setProdForm({ ...prodForm, discount_price: e.target.value })}
                  className="bg-background border-border"
                />
              </div>
              <div className="space-y-2">
                <Label>Tempo (min)</Label>
                <Input
                  type="number"
                  min="0"
                  placeholder="Ex: 15"
                  value={prodForm.preparation_time_min}
                  onChange={(e) => setProdForm({ ...prodForm, preparation_time_min: e.target.value })}
                  className="bg-background border-border"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Select value={prodForm.category_id} onValueChange={(v) => setProdForm({ ...prodForm, category_id: v })}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Selecione uma categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem categoria</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Imagem do produto</Label>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-lg bg-secondary border border-border flex items-center justify-center shrink-0 overflow-hidden">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 space-y-2">
                  <label className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-border bg-background hover:bg-secondary/50 cursor-pointer transition-colors">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {imageFile ? imageFile.name : "Escolher imagem"}
                    </span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelect}
                      className="hidden"
                    />
                  </label>
                  {planLimits.hasFeature("ai_image_generation") && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full h-9 text-xs gap-1.5"
                      disabled={generatingImage || !prodForm.name.trim()}
                      onClick={async () => {
                        setGeneratingImage(true);
                        try {
                          const categoryName = prodForm.category_id && prodForm.category_id !== "none"
                            ? categories.find(c => c.id === prodForm.category_id)?.name
                            : undefined;
                          const { data, error } = await supabase.functions.invoke("generate-product-image", {
                            body: {
                              productName: prodForm.name.trim(),
                              categoryName,
                              restaurantId,
                            },
                          });
                          if (error) throw error;
                          if (data?.image_url) {
                            setProdForm(prev => ({ ...prev, image_url: data.image_url }));
                            setImagePreview(data.image_url);
                            setImageFile(null);
                            toast({ title: "Imagem gerada com IA!" });
                          }
                        } catch (e: any) {
                          toast({ title: "Erro ao gerar imagem", description: e?.message || "Tente novamente", variant: "destructive" });
                        } finally {
                          setGeneratingImage(false);
                        }
                      }}
                    >
                      {generatingImage ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                      Gerar imagem com IA
                    </Button>
                  )}
                  <p className="text-[10px] text-muted-foreground">JPG, PNG ou WebP • Máximo 5MB</p>
                  {imagePreview && (
                    <button
                      type="button"
                      onClick={() => { setImageFile(null); setImagePreview(null); setProdForm({ ...prodForm, image_url: "" }); }}
                      className="text-xs text-destructive hover:underline"
                    >
                      Remover imagem
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label>Disponível</Label>
              <Switch checked={prodForm.is_available} onCheckedChange={(v) => setProdForm({ ...prodForm, is_available: v })} />
            </div>
            <div className="flex items-center justify-between">
              <Label>Destaque</Label>
              <Switch checked={prodForm.is_featured} onCheckedChange={(v) => setProdForm({ ...prodForm, is_featured: v })} />
            </div>

            {/* ========== CATEGORIAS DE ADICIONAIS ========== */}
            <div className="space-y-3 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  <PackagePlus className="h-4 w-4" />
                  Grupos de Adicionais
                </Label>
                <div className="flex items-center gap-1.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setCopyAddonsToForm(true)}
                    className="h-7 text-xs text-primary hover:text-primary/80 gap-1 px-2"
                  >
                    <Copy className="h-3 w-3" /> Copiar de outro
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addAddonCategory} className="h-7 text-xs">
                    <Plus className="h-3 w-3 mr-1" /> Novo Grupo
                  </Button>
                </div>
              </div>
              {addonCategories.filter(c => !c._deleted).length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Nenhum grupo de adicional. Crie um grupo para adicionar opções.</p>
              ) : (
                <div className="space-y-4">
                  {addonCategories.map((cat, catIdx) => {
                    if (cat._deleted) return null;
                    return (
                      <div key={cat.id || `cat-${catIdx}`} className="rounded-xl border border-border bg-secondary/30 p-3 space-y-3">
                        {/* Category header */}
                        <div className="flex items-center gap-2">
                          <Input
                            placeholder="Nome do grupo (ex: Molhos, Extras)"
                            value={cat.name}
                            onChange={(e) => updateAddonCategory(catIdx, "name", e.target.value)}
                            className="h-8 text-sm bg-card border-border flex-1 font-medium"
                          />
                          <button
                            type="button"
                            onClick={() => removeAddonCategory(catIdx)}
                            className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive shrink-0"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {/* Category rules */}
                        <div className="flex items-center gap-3 flex-wrap text-xs">
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Obrigatório</span>
                            <Switch
                              checked={cat.is_required}
                              onCheckedChange={(v) => {
                                updateAddonCategory(catIdx, "is_required", v);
                                if (v && cat.min_select < 1) updateAddonCategory(catIdx, "min_select", 1);
                              }}
                              className="scale-75"
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Mín:</span>
                            <Input
                              type="number"
                              min="0"
                              value={cat.min_select}
                              onChange={(e) => updateAddonCategory(catIdx, "min_select", parseInt(e.target.value) || 0)}
                              className="h-7 w-14 text-xs bg-card border-border text-center"
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Máx:</span>
                            <Input
                              type="number"
                              min="1"
                              value={cat.max_select}
                              onChange={(e) => updateAddonCategory(catIdx, "max_select", parseInt(e.target.value) || 1)}
                              className="h-7 w-14 text-xs bg-card border-border text-center"
                            />
                          </div>
                        </div>
                        {/* Addons in this category */}
                        <div className="space-y-2">
                          {cat.addons.filter(a => !a._deleted).length === 0 && (
                            <p className="text-[11px] text-muted-foreground text-center py-2">Nenhum item neste grupo</p>
                          )}
                          {cat.addons.map((addon, addonIdx) => {
                            if (addon._deleted) return null;
                            const uploadKey = `${catIdx}-${addonIdx}`;
                            return (
                              <div key={addon.id || `addon-${addonIdx}`} className="rounded-lg border border-border bg-card p-2.5 space-y-2">
                                <div className="flex items-center gap-2">
                                  {/* Addon image thumbnail */}
                                  <label className="w-10 h-10 rounded-lg border border-dashed border-border bg-background flex items-center justify-center overflow-hidden cursor-pointer shrink-0 hover:border-primary/50 transition-colors">
                                    {addonImageUploading === uploadKey ? (
                                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    ) : addon.image_url ? (
                                      <img src={addon.image_url} alt="" className="w-full h-full object-cover rounded-lg" />
                                    ) : (
                                      <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
                                    )}
                                    <input
                                      type="file"
                                      accept="image/*"
                                      className="hidden"
                                      onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) handleAddonImageUpload(catIdx, addonIdx, f);
                                      }}
                                    />
                                  </label>
                                  <Input
                                    placeholder="Nome do adicional"
                                    value={addon.name}
                                    onChange={(e) => updateAddonInCategory(catIdx, addonIdx, "name", e.target.value)}
                                    className="h-8 text-sm bg-background border-border flex-1"
                                  />
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="R$"
                                    value={addon.price || ""}
                                    onChange={(e) => updateAddonInCategory(catIdx, addonIdx, "price", parseFloat(e.target.value) || 0)}
                                    className="h-8 text-sm bg-background border-border w-20"
                                  />
                                  <div className="flex items-center gap-1">
                                    <Switch
                                      checked={addon.is_available}
                                      onCheckedChange={(v) => updateAddonInCategory(catIdx, addonIdx, "is_available", v)}
                                      className="scale-75"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeAddonInCategory(catIdx, addonIdx)}
                                      className="p-1 rounded hover:bg-destructive/10 text-destructive shrink-0"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => addAddonToCategory(catIdx)}
                            className="h-7 text-xs w-full text-primary hover:text-primary/80"
                          >
                            <Plus className="h-3 w-3 mr-1" /> Adicionar item
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button onClick={saveProd} disabled={prodSaving || !prodForm.name.trim() || !prodForm.price} className="bg-gradient-primary text-primary-foreground">
              {prodSaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {prodEditing ? "Salvar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DELETE CONFIRM ========== */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir <strong>{deleteTarget?.name}</strong>?
              {deleteTarget?.type === "category" && " Os produtos dessa categoria ficarão sem categoria."}
              {" "}Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Import Menu Dialog */}
      <ImportMenuDialog
        open={importMenuOpen}
        onOpenChange={setImportMenuOpen}
        restaurantId={restaurantId!}
        onImportComplete={() => fetchData(restaurantId!)}
      />

      {/* Copy Addons Dialog */}
      {copyAddonsTarget && (
        <CopyAddonsDialog
          open={!!copyAddonsTarget}
          onOpenChange={(v) => { if (!v) setCopyAddonsTarget(null); }}
          targetProductId={copyAddonsTarget.id}
          targetProductName={copyAddonsTarget.name}
          restaurantId={restaurantId!}
          onCopyComplete={() => fetchData(restaurantId!)}
        />
      )}

      {/* Copy Addons to Form Dialog (inside product modal) */}
      <CopyAddonsDialog
        open={copyAddonsToForm}
        onOpenChange={setCopyAddonsToForm}
        targetProductId={prodEditing?.id || "new"}
        targetProductName={prodForm.name || "Novo Produto"}
        restaurantId={restaurantId!}
        onCopyComplete={() => {}}
        onCopyToForm={(cats) => {
          const newCats: AddonCategory[] = cats.map((c, i) => ({
            name: c.name,
            is_required: c.is_required,
            min_select: c.min_select,
            max_select: c.max_select,
            sort_order: addonCategories.length + i,
            _isNew: true,
            addons: c.addons.map((a, j) => ({
              name: a.name,
              price: a.price,
              is_available: a.is_available,
              image_url: a.image_url,
              sort_order: j,
              _isNew: true,
            })),
          }));
          setAddonCategories(prev => [...prev, ...newCats]);
        }}
      />
    </div>
  );
};

export default GerenciarCardapio;
